import React, { useEffect } from 'react';
import { returnedTypes, measurementType } from './MeasurementEnums';
import { observer } from 'mobx-react-lite';
import useStores from '../../hooks/useStores';
import MeasurementField from './MeasurementField';
import { cmToIn } from './MeasurementOptions';
import {
  CheckIfFirstRender,
  convert,
  isPoundInchType,
  isToCovertValues,
} from './MeasurementHelper';
import { StandardTextFieldProps } from '@mui/material';
import {
  Controller,
  SetValueConfig,
  Control,
  UseFormGetValues,
  FieldValues,
} from 'react-hook-form';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DEFAULT_NS } from '../../i18n/types';

interface MeasurementHeightContainerProps extends StandardTextFieldProps {
  defaultValue?: number;
  returnedType: returnedTypes;
  getValues: UseFormGetValues<FieldValues>;
  isHideMeasurementsIcon?: boolean;
  inputClassName?: string;
  setValue(name: string, value: any, config?: SetValueConfig): void; // eslint-disable-line
  control: Control;
  measureType: measurementType;
  readOnly?: boolean;
  required?: boolean;
  name?: string;
  wrapperClassName?: string;
  textFieldClasses?: {
    root?: string;
    disabled?: string;
    wrapperClassName?: string;
    endAdornmentClassName?: string;
    input?: string;
  };
}

function MeasurementHeightContainer({
  defaultValue,
  returnedType,
  setValue,
  getValues,
  isHideMeasurementsIcon,
  inputClassName,
  control,
  measureType,
  required,
  readOnly = false,
  textFieldClasses,
  name,
  wrapperClassName,
  ...textFieldProps
}: MeasurementHeightContainerProps) {
  const fieldName = name || 'height';
  const { t } = useTranslation(DEFAULT_NS, {
    keyPrefix: 'profile.content.fields',
  });
  const { measurementsStore } = useStores();
  const isMount = CheckIfFirstRender();
  useEffect(() => {
    if (isToCovertValues(isMount, measurementsStore.unitsMapping, measureType)) {
      const convertNumber = convert(
        Number(getValues(fieldName)),
        measureType,
        measurementsStore.unitsMapping,
        Number(getValues('inch')),
      );
      setValue(fieldName, convertNumber, {});
    }
  }, [measurementsStore.unitsMapping]);

  useEffect(() => {
    return () => {
      setValue(fieldName, defaultValue, {});
    };
  }, []);

  return (
    <div className={classNames('flex flex-row items-end', wrapperClassName)}>
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultValue}
        render={({ field: { name, ...props } }) => (
          <MeasurementField
            label={t('height')}
            measureType={measurementType.height}
            returnedType={returnedType}
            getValues={getValues}
            isHideMeasurementsIcon={
              isPoundInchType(measurementsStore.unitsMapping) || isHideMeasurementsIcon
            }
            disabled={readOnly}
            inputClassName={inputClassName}
            textFieldClasses={textFieldClasses}
            name={name}
            dataCy="height-field"
            required={required ?? false}
            {...props}
            placeholder={textFieldProps?.placeholder}
            onBlur={(event) => {
              props.onBlur();
              textFieldProps?.onBlur?.(event);
            }}
            onInput={(event) => {
              textFieldProps?.onInput?.(event);
            }}
            helperText={(textFieldProps?.helperText as string) ?? ''}
            error={textFieldProps?.error ?? false}
          />
        )}
      />

      {isPoundInchType(measurementsStore.unitsMapping) && (
        <Controller
          name={'inch'}
          control={control}
          defaultValue={cmToIn(getValues()[fieldName] || defaultValue)}
          render={({ field: { name, ...props } }) => (
            <MeasurementField
              measureType={measurementType.inch}
              returnedType={returnedType}
              getValues={getValues}
              isHideMeasurementsIcon={isHideMeasurementsIcon}
              inputClassName={inputClassName}
              disabled={readOnly}
              textFieldClasses={textFieldClasses}
              name={name}
              {...props}
              onBlur={(event) => {
                props.onBlur();
                textFieldProps?.onBlur?.(event);
              }}
              onInput={(event) => {
                textFieldProps?.onInput?.(event);
              }}
            />
          )}
        />
      )}
    </div>
  );
}
export default observer(MeasurementHeightContainer);
